import classnames from 'classnames'
import * as React from 'react'

import { isMobile } from '../../../app/shared/isMobile'
import { Icon } from '../icon'
import { List, ListItem, useListState } from '../list'
import { useList } from '../list/hooks'
import { NativeSelect } from '../select/NativeSelect'
import { useToolbarContext } from './hooks'
import { IToolbarItem, ToolbarTheme } from './interfaces'
import { useHoverPopover } from './popover/hooks'
import { Popover } from './popover/popover'
import { PopoverTrigger } from './popover/popover-trigger'
import { ToolbarButton } from './toolbar-button'

export interface ToolbarGroupProps {
  /**
   * Main button item
   */
  defaultItem: IToolbarItem

  /**
   * Shows spinner instead of icon
   */
  isLoading?: boolean

  /**
   * Current selected item id
   */
  isActive: boolean

  /**
   * Should indicate some action in progress
   */
  isInAction?: boolean

  /**
   * Array of items
   */
  groupItems: IToolbarItem[]

  /**
   * Expand group button label
   */
  groupTitle?: string

  /**
   * On item select callback
   */
  onChange: (id: string, trigger: 'button' | 'select' | 'group') => void

  /**
   * Use native select for mobile devices
   *
   * @default true
   */
  useNativeSelect?: boolean

  /**
   * Is item disabled
   */
  disabled?: boolean

  /**
   * Tooltip message if item is disabled
   */
  disabledTooltip?: string
}

export function ToolbarGroup({
  defaultItem,
  isActive,
  isLoading,
  groupItems,
  groupTitle,
  onChange,
  useNativeSelect = isMobile(),
  disabled,
  disabledTooltip,
}: ToolbarGroupProps) {
  const listState = useListState({ currentId: null })
  const { theme, isStretched } = useToolbarContext()
  const [selectedItem, setSelectedItem] = React.useState(defaultItem)
  const groupId = groupTitle?.replace(/\s/g, '-').toLocaleLowerCase()
  const isAlternativeTheme = theme === ToolbarTheme.alternative
  const popoverState = useHoverPopover({
    placement: isAlternativeTheme && isStretched ? 'bottom' : 'right-start',
  })
  const select = useList(
    popoverState,
    selectedItem.id,
    groupItems?.map((i) => ({ value: i.id, label: i.title })) ?? [],
    (option) => {
      setSelectedItem(groupItems![groupItems!.findIndex((item) => item.id === option.value)])
      onChange(option.value, 'select')
    }
  )

  return (
    <div
      className={classnames('relative flex', {
        grow: isAlternativeTheme && isStretched,
        'h-10 w-10': isAlternativeTheme && !isStretched && !FinvizSettings.hasRedesignEnabled,
      })}
    >
      <ToolbarButton
        as={PopoverTrigger}
        state={popoverState}
        contentClass="relative"
        data-testid={`toolbar-group-${groupId}-expand`}
        item={selectedItem}
        active={isActive}
        disabled={disabled}
        title={groupTitle}
        disabledTooltip={disabledTooltip}
        isLoading={isLoading}
        onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
          ev.preventDefault()
          ev.stopPropagation()
          onChange(selectedItem.id, 'group')
        }}
      >
        <Icon name={(isActive && selectedItem.iconActive) || selectedItem.icon} width={32} className="shrink-0" />
        <Icon name="toolExpand" width={32} className="absolute flex" />
      </ToolbarButton>

      {useNativeSelect ? (
        <NativeSelect
          items={groupItems.map((item) => ({ value: item.id, label: item.title }))}
          onChange={(option) => {
            setSelectedItem(groupItems![groupItems!.findIndex((item) => item.id === option.value)])
            onChange(option.value, 'select')
          }}
          onClick={() => onChange(selectedItem.id, 'select')}
        />
      ) : (
        <Popover
          finalFocusRef={{ current: document.body }}
          state={popoverState}
          aria-label={disabled && disabledTooltip ? disabledTooltip : groupTitle}
          hasBackdrop={false}
        >
          <List state={listState} className="grow p-1" onChange={select.onItemChange}>
            {groupItems.map((item) => (
              <ListItem
                key={item.id}
                leftContent={<Icon name={item.icon} className="mr-1.5 shrink-0" width={28} />}
                data-testid={`toolbar-group-${groupId}-${item.id}`}
              >
                {item.title}
              </ListItem>
            ))}
          </List>
        </Popover>
      )}
    </div>
  )
}
